import { useState } from "react";
import { SPECIALIST_ACTIONS } from "@/lib/engineMeta";
import { ConfidenceMeter } from "@/components/ConfidenceMeter";
import { Cpu, ChevronDown, ChevronRight } from "lucide-react";

const DISCIPLINE_COLOR = {
  Textual: "text-emerald-400", Behavioral: "text-purple-300", Geographic: "text-amber-300",
  Reasoning: "text-monk-blue", Digital: "text-cyan-300", Biological: "text-rose-300",
  Visual: "text-pink-300", Active: "text-red-300",
};

function SpecialistCard({ engine, result }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="bg-monk-panel border border-white/10 rounded-md" data-testid={`specialist-${engine.key}`}>
      <div className="px-3 py-2.5 flex items-center gap-2">
        <button onClick={() => result && setOpen(!open)} className="flex items-center gap-2 flex-1 min-w-0 text-left">
          {result ? (open ? <ChevronDown className="w-3.5 h-3.5 text-neutral-500 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 text-neutral-500 shrink-0" />) : <span className="w-3.5 shrink-0" />}
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${result ? "bg-monk-blue" : "bg-neutral-600"}`} />
          <span className="text-[12px] text-neutral-200 truncate">{engine.name}</span>
          <span className={`text-[9px] font-mono ${DISCIPLINE_COLOR[engine.discipline] || "text-neutral-400"} shrink-0`}>{engine.discipline}</span>
        </button>
        {result?.confidence != null
          ? <span className="font-mono text-[10px] text-neutral-500 shrink-0">{result.confidence}%</span>
          : <span className="font-mono text-[9px] text-neutral-600 shrink-0" data-testid={`pending-${engine.key}`}>PENDING</span>}
      </div>
      {result && open && (
        <div className="px-3 pb-3 pt-1 border-t border-white/5 space-y-2 text-[12px]" data-testid={`findings-${engine.key}`}>
          <p className="hard-data text-neutral-200 leading-relaxed">{result.summary}</p>
          {(result.findings || []).slice(0, 4).map((f, i) => (
            <div key={i}><span className="text-monk-blue">•</span> <span className="text-neutral-300">{f.point}</span> {f.basis && <span className="hunch-data">— {f.basis}</span>}</div>
          ))}
          {result.monk_note && <p className="hunch-data">{result.monk_note}</p>}
          <ConfidenceMeter value={result.confidence || 0} />
        </div>
      )}
    </div>
  );
}

export function SpecialistLibrary({ caseData }) {
  const findings = caseData?.case_engines || {};
  const disciplines = [...new Set(SPECIALIST_ACTIONS.map((e) => e.discipline))];
  return (
    <div className="mt-8 mb-4" data-testid="specialist-library">
      <div className="flex items-center gap-3 mb-4">
        <Cpu className="w-4 h-4 text-neutral-400" />
        <div className="overline">Forensic Specialist Library</div>
        <div className="h-px flex-1 bg-white/10" />
        <span className="font-heading font-700 text-sm tracking-tight text-neutral-400">20 Specialist Engines · Auto-fired on every case</span>
      </div>
      <div className="space-y-5">
        {disciplines.map((d) => (
          <div key={d}>
            <div className={`overline mb-2 ${DISCIPLINE_COLOR[d]}`}>{d}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {SPECIALIST_ACTIONS.filter((e) => e.discipline === d).map((e) => (
                <SpecialistCard key={e.key} engine={e} result={findings[e.key]} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
